import React, { useState, useEffect, useRef } from 'react';
import '../../styles/AutomationCard.css';
import { Button, IconButton } from '../';
import { Exit } from '../../assets/icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { Edit02Icon, House01Icon } from '@hugeicons/core-free-icons';
import { colour } from '../../styles/GlobalStyles';
import { getIngestionLogo, getIngestionDisplayName } from './utils';
import { setAutomation, getMlsPropertyCount } from '../../functions';
import MLSConfigForm, { getCountyName } from './MLSConfigForm';

function IngestionSection(props) {
    const { automation, onUpdate, companyId, automationId } = props;
    
    const [ingestionEditOpen, setIngestionEditOpen] = useState(false);
    const [ingestionSourceSelected, setIngestionSourceSelected] = useState(null); // 'mls' or 'ghl' or null (for selection screen)
    const [countyDropdownOpen, setCountyDropdownOpen] = useState(false);
    const [mlsSaving, setMlsSaving] = useState(false);
    const [mlsPropertyCount, setMlsPropertyCount] = useState(null);
    const [mlsPropertyCountLoading, setMlsPropertyCountLoading] = useState(false);
    const mlsCountDebounceRef = useRef(null);
    const mlsCountRequestIdRef = useRef(0);
    
    // Config state
    const [ingestionItems, setIngestionItems] = useState([]);
    
    // Scanner mode state
    const [scannerMode, setScannerMode] = useState(null); // null or 'distressed_cash'
    const [formCountyIds, setFormCountyIds] = useState([]); // multi-select for scanner
    const [formTargetProfit, setFormTargetProfit] = useState('10');
    const [countyMultiDropdownOpen, setCountyMultiDropdownOpen] = useState(false);

    // MLS form state (for adding/editing MLS item)
    const [activeMlsItemId, setActiveMlsItemId] = useState(null); // ID of item being edited
    const [formCountyId, setFormCountyId] = useState('');
    const [formLimit, setFormLimit] = useState('50');
    const [formFilters, setFormFilters] = useState({
        priceMin: '',
        priceMax: '',
        bedsMin: '',
        bedsMax: '',
        bathsMin: '',
        bathsMax: '',
        sqftMin: '',
        sqftMax: '',
        yearBuiltMin: '',
        yearBuiltMax: '',
        daysOnMarketMin: '',
        daysOnMarketMax: '',
        priceChangesMin: '',
        hasPool: false,
        hasGarage: false,
        propertyConditions: [],
    });

    const getNormalizedFormState = () => ({
        countyId: (formCountyId || '').trim(),
        limit: parseInt(formLimit, 10) || 50,
        filters: {
            priceMin: formFilters.priceMin ? parseFloat(formFilters.priceMin) : null,
            priceMax: formFilters.priceMax ? parseFloat(formFilters.priceMax) : null,
            bedsMin: formFilters.bedsMin ? parseInt(formFilters.bedsMin, 10) : null,
            bedsMax: formFilters.bedsMax ? parseInt(formFilters.bedsMax, 10) : null,
            bathsMin: formFilters.bathsMin ? parseFloat(formFilters.bathsMin) : null,
            bathsMax: formFilters.bathsMax ? parseFloat(formFilters.bathsMax) : null,
            sqftMin: formFilters.sqftMin ? parseFloat(formFilters.sqftMin) : null,
            sqftMax: formFilters.sqftMax ? parseFloat(formFilters.sqftMax) : null,
            yearBuiltMin: formFilters.yearBuiltMin ? parseInt(formFilters.yearBuiltMin, 10) : null,
            yearBuiltMax: formFilters.yearBuiltMax ? parseInt(formFilters.yearBuiltMax, 10) : null,
            daysOnMarketMin: formFilters.daysOnMarketMin ? parseInt(formFilters.daysOnMarketMin, 10) : null,
            daysOnMarketMax: formFilters.daysOnMarketMax ? parseInt(formFilters.daysOnMarketMax, 10) : null,
            priceChangesMin: formFilters.priceChangesMin ? parseInt(formFilters.priceChangesMin, 10) : null,
            hasPool: formFilters.hasPool === true,
            hasGarage: formFilters.hasGarage === true,
            propertyConditions: Array.isArray(formFilters.propertyConditions)
                ? [...formFilters.propertyConditions].sort()
                : []
        }
    });

    const getNormalizedItemState = (item) => {
        if (!item) {
            return {
                countyId: '',
                limit: 50,
                filters: {
                    priceMin: null,
                    priceMax: null,
                    bedsMin: null,
                    bedsMax: null,
                    bathsMin: null,
                    bathsMax: null,
                    sqftMin: null,
                    sqftMax: null,
                    yearBuiltMin: null,
                    yearBuiltMax: null,
                    daysOnMarketMin: null,
                    daysOnMarketMax: null,
                    priceChangesMin: null,
                    hasPool: false,
                    hasGarage: false,
                    propertyConditions: []
                }
            };
        }
        const filters = item.filters || {};
        return {
            countyId: item.countyId || '',
            limit: typeof item.limit === 'number' ? item.limit : 50,
            filters: {
                priceMin: filters.priceMin ?? null,
                priceMax: filters.priceMax ?? null,
                bedsMin: filters.bedsMin ?? null,
                bedsMax: filters.bedsMax ?? null,
                bathsMin: filters.bathsMin ?? null,
                bathsMax: filters.bathsMax ?? null,
                sqftMin: filters.sqftMin ?? null,
                sqftMax: filters.sqftMax ?? null,
                yearBuiltMin: filters.yearBuiltMin ?? null,
                yearBuiltMax: filters.yearBuiltMax ?? null,
                daysOnMarketMin: filters.daysOnMarketMin ?? null,
                daysOnMarketMax: filters.daysOnMarketMax ?? null,
                priceChangesMin: filters.priceChangesMin ?? null,
                hasPool: filters.hasPool === true,
                hasGarage: filters.hasGarage === true,
                propertyConditions: Array.isArray(filters.propertyConditions)
                    ? [...filters.propertyConditions].sort()
                    : []
            }
        };
    };

    const generateIngestionId = () => `ingest_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

    // Initialize ingestion items from automation data
    useEffect(() => {
        if (!automation) return;
        
        let items = [];
        if (Array.isArray(automation.ingestion)) {
            items = automation.ingestion.map((item, index) => {
                if (!item) return item;
                if (item.id) return item;
                return { ...item, id: generateIngestionId() };
            });
        }
        setIngestionItems(items);
    }, [automation]);

    // Fetch MLS property count when filters or county change (debounced)
    useEffect(() => {
        if (ingestionSourceSelected !== 'mls' || !formCountyId?.trim()) {
            setMlsPropertyCount(null);
            setMlsPropertyCountLoading(false);
            return;
        }

        if (mlsCountDebounceRef.current) {
            clearTimeout(mlsCountDebounceRef.current);
        }

        mlsCountDebounceRef.current = setTimeout(() => {
            mlsCountDebounceRef.current = null;

            const buildFiltersForApi = () => {
                const f = {};
                if (formFilters.priceMin) f.priceMin = parseFloat(formFilters.priceMin);
                if (formFilters.priceMax) f.priceMax = parseFloat(formFilters.priceMax);
                if (formFilters.bedsMin) f.bedsMin = parseInt(formFilters.bedsMin, 10);
                if (formFilters.bedsMax) f.bedsMax = parseInt(formFilters.bedsMax, 10);
                if (formFilters.bathsMin) f.bathsMin = parseFloat(formFilters.bathsMin);
                if (formFilters.bathsMax) f.bathsMax = parseFloat(formFilters.bathsMax);
                if (formFilters.sqftMin) f.sqftMin = parseFloat(formFilters.sqftMin);
                if (formFilters.sqftMax) f.sqftMax = parseFloat(formFilters.sqftMax);
                if (formFilters.yearBuiltMin) f.yearBuiltMin = parseInt(formFilters.yearBuiltMin, 10);
                if (formFilters.yearBuiltMax) f.yearBuiltMax = parseInt(formFilters.yearBuiltMax, 10);
                if (formFilters.daysOnMarketMin) f.daysOnMarketMin = parseInt(formFilters.daysOnMarketMin, 10);
                if (formFilters.daysOnMarketMax) f.daysOnMarketMax = parseInt(formFilters.daysOnMarketMax, 10);
                if (formFilters.priceChangesMin) f.priceChangesMin = parseInt(formFilters.priceChangesMin, 10);
                if (formFilters.hasPool) f.hasPool = true;
                if (formFilters.hasGarage) f.hasGarage = true;
                if (formFilters.propertyConditions?.length) f.propertyConditions = formFilters.propertyConditions;
                return f;
            };

            const thisRequestId = ++mlsCountRequestIdRef.current;
            setMlsPropertyCountLoading(true);
            getMlsPropertyCount(formCountyId.trim(), buildFiltersForApi())
                .then((result) => {
                    if (thisRequestId !== mlsCountRequestIdRef.current) return;
                    if (result.status === 200 && typeof result.count === 'number') {
                        setMlsPropertyCount(result.count);
                    } else {
                        setMlsPropertyCount(null);
                    }
                })
                .catch(() => {
                    if (thisRequestId !== mlsCountRequestIdRef.current) return;
                    setMlsPropertyCount(null);
                })
                .finally(() => {
                    if (thisRequestId !== mlsCountRequestIdRef.current) return;
                    setMlsPropertyCountLoading(false);
                });
        }, 400);

        return () => {
            if (mlsCountDebounceRef.current) {
                clearTimeout(mlsCountDebounceRef.current);
                mlsCountDebounceRef.current = null;
            }
        };
    }, [ingestionSourceSelected, formCountyId, formFilters]);

    const handleStartEditMls = (item) => {
        // Scanner mode detection
        if (item.type === 'distressed_scanner' || item.scannerMode === 'distressed_cash') {
            setScannerMode('distressed_cash');
            setFormCountyIds(Array.isArray(item.countyIds) ? [...item.countyIds] : (item.countyId ? [item.countyId] : []));
            setFormTargetProfit(item.targetProfitPercentage != null ? String(item.targetProfitPercentage) : '10');
        } else {
            setScannerMode(null);
            setFormCountyIds([]);
            setFormTargetProfit('10');
        }

        setFormCountyId(item.countyId || '');
        setFormLimit(item.limit?.toString() || '50');
        const filters = item.filters || {};
        setFormFilters({
            priceMin: filters.priceMin?.toString() ?? '',
            priceMax: filters.priceMax?.toString() ?? '',
            bedsMin: filters.bedsMin?.toString() ?? '',
            bedsMax: filters.bedsMax?.toString() ?? '',
            bathsMin: filters.bathsMin?.toString() ?? '',
            bathsMax: filters.bathsMax?.toString() ?? '',
            sqftMin: filters.sqftMin?.toString() ?? '',
            sqftMax: filters.sqftMax?.toString() ?? '',
            yearBuiltMin: filters.yearBuiltMin?.toString() ?? '',
            yearBuiltMax: filters.yearBuiltMax?.toString() ?? '',
            daysOnMarketMin: filters.daysOnMarketMin?.toString() ?? '',
            daysOnMarketMax: filters.daysOnMarketMax?.toString() ?? '',
            priceChangesMin: filters.priceChangesMin?.toString() ?? '',
            hasPool: filters.hasPool === true,
            hasGarage: filters.hasGarage === true,
            propertyConditions: Array.isArray(filters.propertyConditions) ? [...filters.propertyConditions] : [],
        });
        setActiveMlsItemId(item.id);
        setIngestionSourceSelected('mls');
    };

    const updateMlsFilter = (field, value) => {
        setFormFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleTogglePropertyCondition = (condition) => {
        setFormFilters(prev => {
            const arr = prev.propertyConditions || [];
            const isSelected = arr.includes(condition);
            return {
                ...prev,
                propertyConditions: isSelected ? arr.filter(c => c !== condition) : [...arr, condition]
            };
        });
    };

    const handleSaveMlsConfig = async () => {
        if (!companyId || !automationId) return;

        const isScanner = scannerMode === 'distressed_cash';

        // Validation
        if (isScanner) {
            if (!formCountyIds || formCountyIds.length === 0) {
                alert('Please select at least one county.');
                return;
            }
        } else {
            if (!formCountyId) {
                alert('Please select a county.');
                return;
            }
        }

        setMlsSaving(true);
        try {
            const filtersToSave = {};
            if (formFilters.priceMin) filtersToSave.priceMin = parseFloat(formFilters.priceMin);
            if (formFilters.priceMax) filtersToSave.priceMax = parseFloat(formFilters.priceMax);
            if (formFilters.bedsMin) filtersToSave.bedsMin = parseInt(formFilters.bedsMin, 10);
            if (formFilters.bedsMax) filtersToSave.bedsMax = parseInt(formFilters.bedsMax, 10);
            if (formFilters.bathsMin) filtersToSave.bathsMin = parseFloat(formFilters.bathsMin);
            if (formFilters.bathsMax) filtersToSave.bathsMax = parseFloat(formFilters.bathsMax);
            if (formFilters.sqftMin) filtersToSave.sqftMin = parseFloat(formFilters.sqftMin);
            if (formFilters.sqftMax) filtersToSave.sqftMax = parseFloat(formFilters.sqftMax);

            if (!isScanner) {
                // Standard-only filters
                if (formFilters.yearBuiltMin) filtersToSave.yearBuiltMin = parseInt(formFilters.yearBuiltMin, 10);
                if (formFilters.yearBuiltMax) filtersToSave.yearBuiltMax = parseInt(formFilters.yearBuiltMax, 10);
                if (formFilters.daysOnMarketMin) filtersToSave.daysOnMarketMin = parseInt(formFilters.daysOnMarketMin, 10);
                if (formFilters.daysOnMarketMax) filtersToSave.daysOnMarketMax = parseInt(formFilters.daysOnMarketMax, 10);
                if (formFilters.priceChangesMin) filtersToSave.priceChangesMin = parseInt(formFilters.priceChangesMin, 10);
                if (formFilters.hasPool) filtersToSave.hasPool = true;
                if (formFilters.hasGarage) filtersToSave.hasGarage = true;
                if (formFilters.propertyConditions?.length) filtersToSave.propertyConditions = formFilters.propertyConditions;
            }

            let newItem;
            if (isScanner) {
                newItem = {
                    id: activeMlsItemId || `ingest_${Date.now()}`,
                    type: 'distressed_scanner',
                    scannerMode: 'distressed_cash',
                    countyIds: formCountyIds,
                    targetProfitPercentage: Math.min(100, Math.max(1, parseFloat(formTargetProfit) || 10)),
                    filters: filtersToSave
                };
            } else {
                newItem = {
                    id: activeMlsItemId || `ingest_${Date.now()}`,
                    type: 'mls_buy_box',
                    countyId: formCountyId,
                    limit: parseInt(formLimit, 10) || 50,
                    filters: filtersToSave
                };
            }

            let newItems;
            if (activeMlsItemId) {
                newItems = ingestionItems.map(i => i.id === activeMlsItemId ? newItem : i);
            } else {
                // Temporarily disabled until we add the ability to support multiple ingestion systems natively in the UI
                // newItems = [...ingestionItems, newItem];
                setMlsSaving(false);
                return;
            }

            const payload = { ingestion: newItems };
            const result = await setAutomation(companyId, automationId, payload);

            if (result.status === 200 && onUpdate) {
                onUpdate({ ...automation, ...payload });
                setIngestionSourceSelected(null); // Return to list view
            } else if (result.status !== 200) {
                alert(result.message || 'Failed to save MLS config. Please try again.');
            }
        } catch (error) {
            console.error('Error saving MLS config:', error);
            alert('Failed to save MLS config. Please try again.');
        } finally {
            setMlsSaving(false);
        }
    };

    const handleToggleEdit = () => {
        if (ingestionEditOpen) {
            setIngestionEditOpen(false);
            setIngestionSourceSelected(null);
        } else {
            setIngestionEditOpen(true);
        }
    };

    const isMissingValue = (value) => (
        value === undefined ||
        value === null ||
        (typeof value === 'string' && value.trim() === '') ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' && !Array.isArray(value) && value && Object.keys(value).length === 0)
    );

    const currentMlsItem = ingestionItems.find(i => i.id === activeMlsItemId);
    const hasMlsChanges = JSON.stringify(getNormalizedFormState())
        !== JSON.stringify(getNormalizedItemState(currentMlsItem));
    const hasUnsavedDefaults = !currentMlsItem ||
        isMissingValue(currentMlsItem.countyId) ||
        isMissingValue(currentMlsItem.limit);
    const saveDisabled = !hasMlsChanges && !hasUnsavedDefaults;

    return (
        <div className={`automation-form-section-card automation-detail-section-card ${ingestionEditOpen ? '' : 'automation-form-section-card-compact'}`}>
            <div className="automation-form-section-card-content">
                <div className="automation-form-section-card-text">
                    <span className="body-bold colour-primary">Ingestion</span>
                    {ingestionItems.length === 0 && (
                        <span className="body-regular colour-secondary">Configure data sources.</span>
                    )}
                </div>
                <div className="automation-form-section-card-icon-button">
                    <IconButton
                        size="medium"
                        icon={ingestionEditOpen ? <Exit fill={colour.red03} size={14} clickFunc={null} /> : <HugeiconsIcon icon={Edit02Icon} color={colour.gray06} size={16} />}
                        disabled={false}
                        clickFunc={handleToggleEdit}
                        standardIcon={false}
                        background={true}
                        bgType={ingestionEditOpen ? "destructive" : "white"}
                    />
                </div>
            </div>
            
            {ingestionEditOpen ? (
                <div className="automation-ingestion-edit-panel">
                    {ingestionSourceSelected === 'mls' ? (
                        <MLSConfigForm
                            formCountyId={formCountyId}
                            setFormCountyId={setFormCountyId}
                            formLimit={formLimit}
                            setFormLimit={setFormLimit}
                            formFilters={formFilters}
                            updateMlsFilter={updateMlsFilter}
                            handleTogglePropertyCondition={handleTogglePropertyCondition}
                            countyDropdownOpen={countyDropdownOpen}
                            setCountyDropdownOpen={setCountyDropdownOpen}
                            mlsSaving={mlsSaving}
                            onSave={handleSaveMlsConfig}
                            saveDisabled={saveDisabled}
                            mlsPropertyCount={mlsPropertyCount}
                            mlsPropertyCountLoading={mlsPropertyCountLoading}
                            onCancel={() => setIngestionSourceSelected(null)}
                            onChangeSource={() => setIngestionSourceSelected(null)}
                            scannerMode={scannerMode}
                            setScannerMode={setScannerMode}
                            formCountyIds={formCountyIds}
                            setFormCountyIds={setFormCountyIds}
                            formTargetProfit={formTargetProfit}
                            setFormTargetProfit={setFormTargetProfit}
                            countyMultiDropdownOpen={countyMultiDropdownOpen}
                            setCountyMultiDropdownOpen={setCountyMultiDropdownOpen}
                        />
                    ) : (
                        // List View
                        <div className="automation-ingestion-list-view">
                            
                            {ingestionItems.map((item, index) => (
                                <div key={item.id || index} className="automation-ingestion-item-row">
                                    <div className="automation-ingestion-item-info">
                                        {(item.type === 'mls_buy_box' || item.type === 'distressed_scanner') ? (
                                            <>
                                                <HugeiconsIcon icon={House01Icon} size={20} color={colour.gray06} />
                                                <div className="automation-ingestion-item-details">
                                                    <span className="body-regular colour-primary">
                                                        {item.type === 'distressed_scanner'
                                                            ? `Distressed Scanner — ${(item.countyIds || []).length} ${(item.countyIds || []).length === 1 ? 'county' : 'counties'}`
                                                            : `${getCountyName(item.countyId)} — MLS`}
                                                    </span>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                {getIngestionLogo(false, item.type)}
                                                <div className="automation-ingestion-item-details">
                                                    <span className="body-regular colour-primary">{getIngestionDisplayName(item.type)}</span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    {
                                        (item.type === 'mls_buy_box' || item.type === 'distressed_scanner') &&
                                        <div className="automation-ingestion-item-actions">
                                            <Button
                                                buttonType="secondary-button"
                                                size="small"
                                                text="Select"
                                                cta={() => handleStartEditMls(item)}
                                                disabled={false}
                                                leftIcon={null}
                                                leftIconClass=""
                                                rightIcon={null}
                                                rightIconClass=""
                                                alt=""
                                                standardIcon={false}
                                                badge={false}
                                                badgeVal={null}
                                                badgeBG={null}
                                                buttonRef={null}
                                                spinner={false}
                                            />
                                        </div>
                                    }
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <div className="automation-detail-section-summary automation-ingestion-summary">
                    <div className="automation-detail-section-summary-content">
                        {ingestionItems.length > 0 ? (
                            ingestionItems.map((item, i) => (
                                <div key={i} className="automation-ingestion-summary-row">
                                    {(item.type === 'mls_buy_box' || item.type === 'distressed_scanner') ? (
                                        <HugeiconsIcon icon={House01Icon} size={16} color={colour.gray04} />
                                    ) : (
                                        getIngestionLogo(false, item.type)
                                    )}
                                    <span className="body-regular colour-secondary">
                                        {item.type === 'distressed_scanner'
                                            ? `Distressed Seller Scanner — ${(item.countyIds || []).length} counties, top 25 daily.`
                                            : item.type === 'mls_buy_box'
                                                ? `${getCountyName(item.countyId)} — MLS listings.`
                                                : getIngestionDisplayName(item.type)}
                                    </span>
                                </div>
                            ))
                        ) : (
                            <span className="body-regular colour-secondary">No ingestion sources configured.</span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default IngestionSection;
